// hayah/SwipeRow — shared swipe-left-to-delete row for Hayah lists.
//
// Standardised UX for any deletable item in Hayah surfaces (conversation
// history, future fasting/weight/etc. lists). Swipe left past -72px reveals
// a coral DELETE pad; release past that threshold fires onDelete with a
// slide-off animation + haptic. Cancel by releasing earlier or sliding back.
//
// Designed to be drop-in: wrap any row element with <SwipeRow onDelete={...}
// onTap={...}>{children}</SwipeRow>. onTap is preferred over the children's
// own onClick because the gesture system needs to distinguish a tap from a
// swipe (a moved pointer-up should never count as a tap).
//
// The richer CalorieCounter SwipeRow (with quick-add + re-log tiers) is
// not replaced — this is the simpler delete-only variant used everywhere
// else. Both share the same coral-from-the-right delete affordance, the
// same -72px threshold, and the same haptic taps so they feel identical.

import React, { useRef, useState } from "react";
import { hapticHayahSelection, hapticHayahSwipe } from "../../hayahHaptics";

const DELETE_T = -72;

export default function SwipeRow({
  children,
  onDelete,
  onTap,
  // Coral by default — matches the danger affordance used across Hayah.
  // Callers can pass a different tone if a surface needs it.
  danger = "#ff8a65",
  // Disable the gesture (e.g., row already in edit mode).
  disabled = false,
  // Optional aria label for the gesture region.
  ariaLabel,
}) {
  const [dx, setDx] = useState(0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTRef = useRef(0);
  const movedRef = useRef(false);
  const lockedRef = useRef(null); // "horizontal" | "vertical" | null
  const lastTierRef = useRef(0);

  const tierFor = (x) => (x <= DELETE_T ? -1 : 0);

  const onPointerDown = (e) => {
    if (disabled) return;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startTRef.current = Date.now();
    movedRef.current = false;
    lockedRef.current = null;
    lastTierRef.current = 0;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (disabled) return;
    if (!startTRef.current) return;
    const ddx = e.clientX - startXRef.current;
    const ddy = e.clientY - startYRef.current;
    if (!lockedRef.current) {
      if (Math.abs(ddx) > 6 && Math.abs(ddx) > Math.abs(ddy)) lockedRef.current = "horizontal";
      else if (Math.abs(ddy) > 6) lockedRef.current = "vertical";
    }
    if (lockedRef.current === "horizontal") {
      e.preventDefault();
      movedRef.current = true;
      // Right side has no action — clamp slightly to allow rubber-band feel.
      const clamped = Math.max(-120, Math.min(20, ddx));
      setDx(clamped);
      const tier = tierFor(clamped);
      if (tier !== lastTierRef.current) {
        try { hapticHayahSelection(); } catch {}
        lastTierRef.current = tier;
      }
    }
  };
  const onPointerUp = () => {
    if (disabled) return;
    if (movedRef.current) {
      const tier = tierFor(dx);
      if (tier === -1 && onDelete) {
        setDx(-360);
        try { hapticHayahSwipe(); } catch {}
        setTimeout(() => onDelete(), 180);
      } else {
        setDx(0);
      }
    } else {
      setDx(0);
    }
    startTRef.current = 0;
    lockedRef.current = null;
  };
  const onClick = (e) => {
    if (movedRef.current) { e.preventDefault(); e.stopPropagation(); return; }
    onTap && onTap();
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel || (onDelete ? "Item — swipe left to delete" : undefined)}
      style={{ position: "relative", overflow: "hidden", borderRadius: 12 }}
    >
      {/* Left-reveal DELETE pad */}
      <div style={{
        position: "absolute", inset: 0,
        background: `linear-gradient(90deg, transparent 30%, ${danger}88 70%, ${danger} 100%)`,
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        padding: "0 18px", borderRadius: 12,
        opacity: dx < -8 ? 1 : 0, transition: "opacity 0.15s ease",
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: "0.14em", fontFamily: "JetBrains Mono, ui-monospace, monospace" }}>DELETE</span>
      </div>
      <div
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          transform: `translateX(${dx}px)`,
          transition: dx === 0 || dx === -360 ? "transform 0.22s cubic-bezier(0.22,0.61,0.36,1)" : "none",
          touchAction: "pan-y",
          position: "relative",
        }}
      >
        {children}
      </div>
    </div>
  );
}
